// src/pages/portal/PortalEstimateDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { usePortalAuth } from '../../context/PortalAuthContext';
import PortalLayout from '../../components/PortalLayout';

function fmt(amount) {
  if (amount == null) return '—';
  return '$' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + (d.length === 10 ? 'T12:00:00' : '')).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// Parse sections_data and extract client-visible line items
// Baked-in items are hidden, overhead sections are excluded entirely
function extractClientLineItems(sectionsData) {
  if (!sectionsData) return [];
  try {
    const parsed = JSON.parse(sectionsData);
    const sections = parsed.sections || [];
    const result = [];
    for (const sec of sections) {
      // Skip overhead sections
      if (sec.is_overhead) continue;
      const visibleItems = (sec.items || []).filter(i =>
        !i.baked_in &&
        (i.description || i.unit_cost) &&
        i.item_type !== 'Overhead'
      );
      if (visibleItems.length === 0 && !sec.section_description) {
        // Section is fully baked — show just the section total
        const secTotal = (sec.items || []).reduce((sum, i) => {
          const cost = parseFloat(i.unit_cost || 0) * parseFloat(i.qty || 0)
          const type = i.item_type || 'Material'
          if (type === 'Labor' || type === 'Overhead') return sum + cost
          const markup = i.markup_type === 'custom' ? parseFloat(i.custom_markup || 0) : parseFloat(i.markup_pct ?? 100)
          return sum + cost * (1 + markup / 100)
        }, 0)
        result.push({
          _id: sec._id,
          isSection: true,
          name: sec.name,
          description: sec.section_description || '',
          optional: sec.client_optional || false,
          total: secTotal,
          items: []
        });
      } else {
        result.push({
          _id: sec._id,
          isSection: true,
          name: sec.name,
          description: sec.section_description || '',
          optional: sec.client_optional || false,
          items: visibleItems.map(item => {
            const cost = parseFloat(item.unit_cost || 0) * parseFloat(item.qty || 0)
            const type = item.item_type || 'Material'
            let clientPrice = cost
            if (type !== 'Labor' && type !== 'Overhead') {
              const markup = item.markup_type === 'custom' ? parseFloat(item.custom_markup || 0) : parseFloat(item.markup_pct ?? 100)
              clientPrice = cost * (1 + markup / 100)
            }
            return {
              _id: item._id,
              description: item.description,
              qty: parseFloat(item.qty || 1),
              unit: item.unit || 'ea',
              clientPrice
            }
          })
        });
      }
    }
    return result;
  } catch (e) {
    return [];
  }
}

export default function PortalEstimateDetail() {
  const { id } = useParams();
  const { clientRecord } = usePortalAuth();
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!clientRecord?.id) return;
    async function load() {
      const { data: est } = await supabase
        .from('estimates')
        // Select only client-safe fields — never cost, markup, profit, margin
        .select('id, title, quote_number, suggested_price, status, created_at, expiry_date, scope_text, client_notes, sections_data, deposit_pct, deposit_amount')
        .eq('id', id)
        .eq('client_id', clientRecord.id)
        .in('status', ['sent', 'approved', 'declined']) // Security: only show client-facing statuses
        .single();
      if (!est) { navigate('/portal/estimates'); return; }
      setEstimate(est);
      // Parse line items from sections_data — no separate table needed
      if (est.sections_data) {
        setSections(extractClientLineItems(est.sections_data));
      }
      setLoading(false);
    }
    load();
  }, [id, clientRecord]);

  async function handleAction(newStatus) {
    setActionLoading(true);
    const { error } = await supabase
      .from('estimates')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('client_id', clientRecord.id);
    if (!error) {
      setEstimate(prev => ({ ...prev, status: newStatus }));
      setMessage(newStatus === 'approved'
        ? '✓ Estimate approved! We\'ll be in touch shortly to schedule your project.'
        : 'Estimate declined. Contact us if you have questions or would like to discuss options.');
    }
    setActionLoading(false);
  }

  const canAct = estimate && estimate.status?.toLowerCase() === 'sent';
  const depositAmt = estimate?.deposit_amount || (estimate?.suggested_price && estimate?.deposit_pct
    ? estimate.suggested_price * (estimate.deposit_pct / 100) : 0);

  if (loading) return <PortalLayout><div style={{ color: '#aaa', fontSize: '14px', paddingTop: '40px' }}>Loading…</div></PortalLayout>;
  if (!estimate) return null;

  return (
    <PortalLayout>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '20px', fontSize: '13px', color: '#999' }}>
        <Link to="/portal/estimates" style={{ color: '#c8a96e', textDecoration: 'none' }}>Estimates</Link>
        {' → '}
        <span>{estimate.title || (estimate.quote_number ? `Quote #${estimate.quote_number}` : 'Estimate')}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '24px', alignItems: 'start' }}>
        {/* Main content */}
        <div>
          <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', padding: '32px', marginBottom: '20px' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
              <div>
                <h1 style={{ fontSize: '20px', fontWeight: '600', color: '#1a1a18', margin: '0 0 4px' }}>
                  {estimate.title || (estimate.quote_number ? `Quote #${estimate.quote_number}` : 'Your Estimate')}
                </h1>
                <div style={{ fontSize: '13px', color: '#888' }}>Issued {fmtDate(estimate.created_at)}</div>
                {estimate.expiry_date && (
                  <div style={{ fontSize: '13px', color: '#888', marginTop: '2px' }}>
                    Valid until {fmtDate(estimate.expiry_date)}
                  </div>
                )}
              </div>
              <StatusPill status={estimate.status} />
            </div>

            {/* Scope of work - client_notes, not internal notes */}
            {(estimate.scope_text || estimate.client_notes) && (
              <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #f0ece4' }}>
                {estimate.scope_text || estimate.client_notes}
              </div>
            )}

            {/* Line items from sections_data */}
            {sections.length > 0 ? (
              <div>
                {sections.map((sec, si) => (
                  <div key={sec._id || si} style={{ marginBottom: '20px' }}>
                    {/* Section header */}
                    <div style={{ padding: '8px 0', borderBottom: '1px solid #e8e4dc', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: '#2d4a2d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {sec.name}{sec.optional ? ' (Optional)' : ''}
                      </span>
                      {sec.description && (
                        <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0', lineHeight: '1.5' }}>{sec.description}</p>
                      )}
                    </div>
                    {/* Line items */}
                    {sec.items.length > 0 ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <tbody>
                          {sec.items.map((item, ii) => (
                            <tr key={item._id || ii} style={{ borderBottom: '1px solid #f6f3ee' }}>
                              <td style={{ padding: '8px 10px', color: '#1a1a18', fontWeight: '500' }}>{item.description}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', color: '#888', width: '80px' }}>{item.qty} {item.unit}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600', color: '#1a1a18', width: '100px' }}>{fmt(item.clientPrice)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={2} style={{ padding: '8px 10px', textAlign: 'right', fontSize: '12px', color: '#888', borderTop: '1px solid #e8e4dc' }}>Section Total</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: '#1a2e1a', borderTop: '1px solid #e8e4dc' }}>
                              {fmt(sec.items.reduce((s, i) => s + i.clientPrice, 0))}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    ) : (
                      // Fully baked section — show description + total only
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: '#f9fafb', borderRadius: '6px' }}>
                        <span style={{ fontSize: '13px', color: '#555' }}>{sec.description || 'Includes all materials and labor'}</span>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#1a2e1a' }}>{fmt(sec.total)}</span>
                      </div>
                    )}
                  </div>
                ))}

                {/* Total */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '2px solid #1a2e1a', marginTop: '8px' }}>
                  <div style={{ width: '240px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: '700', color: '#1a1a18' }}>
                      <span>Total</span>
                      <span>{fmt(estimate.suggested_price)}</span>
                    </div>
                    {depositAmt > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#888', marginTop: '8px' }}>
                        <span>Deposit to begin ({estimate.deposit_pct || 50}%)</span>
                        <span style={{ fontWeight: '600', color: '#2d4a2d' }}>{fmt(depositAmt)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // No sections data — show total only
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '16px', borderTop: '1px solid #f0ece4' }}>
                <span style={{ fontWeight: '600', color: '#1a1a18' }}>Estimate Total</span>
                <span style={{ fontWeight: '700', fontSize: '18px', color: '#1a1a18' }}>{fmt(estimate.suggested_price)}</span>
              </div>
            )}
          </div>

          {/* Client notes (NOT internal notes) */}
          {estimate.client_notes && (
            <div style={{ background: '#faf8f4', border: '1px solid #e8e4dc', borderRadius: '8px', padding: '20px 24px' }}>
              <div style={{ fontSize: '11px', color: '#999', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: '600', marginBottom: '8px' }}>Notes</div>
              <p style={{ fontSize: '13.5px', color: '#555', lineHeight: '1.65', margin: 0 }}>{estimate.client_notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div>
          {/* Action card */}
          <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', padding: '24px', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a18', marginBottom: '16px' }}>Actions</div>
            {message ? (
              <div style={{ fontSize: '13.5px', color: '#16a34a', lineHeight: '1.5', padding: '12px', background: '#e8f7ee', borderRadius: '7px' }}>{message}</div>
            ) : canAct ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button onClick={() => handleAction('approved')} disabled={actionLoading} style={approveBtn}>
                  {actionLoading ? '…' : '✓ Approve Estimate'}
                </button>
                <button onClick={() => handleAction('declined')} disabled={actionLoading} style={declineBtn}>
                  Decline
                </button>
                <p style={{ fontSize: '11px', color: '#aaa', margin: 0, lineHeight: '1.5' }}>
                  Approving this estimate authorizes TLC Unlimited to proceed with the work described.
                </p>
              </div>
            ) : (
              <div style={{ fontSize: '13px', color: '#aaa' }}>
                This estimate has been <strong>{estimate.status}</strong>.
              </div>
            )}
          </div>

          {/* Summary */}
          <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', padding: '20px 24px' }}>
            <SummaryRow label="Status" value={<StatusPill status={estimate.status} />} />
            <SummaryRow label="Issued" value={fmtDate(estimate.created_at)} />
            {estimate.expiry_date && <SummaryRow label="Valid Until" value={fmtDate(estimate.expiry_date)} />}
            <SummaryRow label="Total" value={<strong>{fmt(estimate.suggested_price)}</strong>} />
            {depositAmt > 0 && <SummaryRow label="Deposit Required" value={<strong style={{ color: '#2d4a2d' }}>{fmt(depositAmt)}</strong>} />}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}

function StatusPill({ status }) {
  const map = {
    sent:     { bg: '#e8f0ff', color: '#2563eb', label: 'Awaiting Review' },
    approved: { bg: '#e8f7ee', color: '#16a34a', label: 'Approved' },
    declined: { bg: '#fdf0f0', color: '#dc2626', label: 'Declined' },
  };
  const s = map[status?.toLowerCase()] || { bg: '#f0f0ec', color: '#666', label: status };
  return <span style={{ background: s.bg, color: s.color, padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{s.label}</span>;
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f6f3ee', fontSize: '13px', color: '#666' }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

const approveBtn = {
  background: '#1a2e1a', color: 'white', border: 'none', borderRadius: '7px',
  padding: '11px', fontSize: '13.5px', fontWeight: '600', cursor: 'pointer',
  fontFamily: 'inherit', letterSpacing: '0.01em', width: '100%',
};
const declineBtn = {
  background: 'transparent', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '7px',
  padding: '11px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', width: '100%',
};
